import React, { useEffect, useState, useMemo } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Title } from '@/common/title/Title';
import { MainBox } from '@/common/layout/MainBox';
import { SpacerLayout, Spacer } from '@/common/layout/SpacerLayout';
import { TestKpiStrip } from './charts/TestKpiStrip';
import { StatusColumn } from './StatusColumn';
import { useRsrc } from '@/common/rsrc/RsrcContext';

export const TestDashboard = () => {
  const rsrc = useRsrc();
  const [data, setData] = useState(null);
  const [selectedSuite, setSelectedSuite] = useState('ALL');
  const [selectedTest, setSelectedTest] = useState('ALL');
  const [selectedRequirement, setSelectedRequirement] = useState('ALL');

  useEffect(() => {
    rsrc
      .data('ac', 'TEST_DASHBOARD_RESULTS_DATA_PLUGIN')
      .then((res) => { setData(res ?? null); });
  }, []);

  // Filter suites
  const filteredSuites = useMemo(() => {
    if (!data?.suites) return [];
    if (selectedSuite === 'ALL') return data.suites;
    return data.suites.filter(s => s.code === selectedSuite);
  }, [data, selectedSuite]);

  // All tests for selected suites
  const availableTests = useMemo(() => {
    if (!data?.suites) return [];
    const suites = selectedSuite === 'ALL' ? data.suites : data.suites.filter(s => s.code === selectedSuite);
    return suites.flatMap(s => s.tests || []);
  }, [data, selectedSuite]);

  // Filter tests
  const filteredTests = useMemo(() => {
    if (selectedTest === 'ALL') return availableTests;
    return availableTests.filter(t => t.code === selectedTest);
  }, [availableTests, selectedTest]);

  // All requirements for slected tests
  const availableRequirements = useMemo(() => {
    const tests = selectedTest === 'ALL' ? availableTests : availableTests.filter(t => t.code === selectedTest);
    return tests.flatMap(t => t.requirements || []);
  }, [availableTests, selectedTest]);

  // Filter requirements
  const filteredRequirements = useMemo(() => {
    if (selectedRequirement === 'ALL') return availableRequirements;
    return availableRequirements.filter(r => r.code === selectedRequirement);
  }, [availableRequirements, selectedRequirement]);

  // Counts
  const suitesData = useMemo(() => {
    if (!filteredSuites.length) return null;
    
    const counts = filteredSuites.reduce((acc, s) => {
      const status = s.status?.toUpperCase();
      if (status === 'SUCCESS') acc.SUCCESS++;
      else if (status === 'FAIL') acc.FAIL++;
      else acc.NOT_RUN++;
      acc.TOTAL++;
      return acc;
    }, { SUCCESS: 0, FAIL: 0, NOT_RUN: 0, TOTAL: 0 });

    return {
      items: filteredSuites.map(s => ({
        code: s.code,
        name: s.name,
        status: s.status,
      })),
      counts,
    };
  }, [filteredSuites]);

  const testsData = useMemo(() => {
    if (!filteredTests.length) return null;
    
    const counts = filteredTests.reduce((acc, t) => {
      const status = t.status?.toUpperCase();
      if (status === 'SUCCESS') acc.SUCCESS++;
      else if (status === 'FAIL') acc.FAIL++;
      else acc.NOT_RUN++;
      acc.TOTAL++;
      return acc;
    }, { SUCCESS: 0, FAIL: 0, NOT_RUN: 0, TOTAL: 0 });

    return {
      items: filteredTests.map(t => ({
        code: t.code,
        name: t.name,
        status: t.status,
      })),
      counts,
    };
  }, [filteredTests]);

  const requirementsData = useMemo(() => {
    if (!filteredRequirements.length) return null;
    
    const counts = filteredRequirements.reduce((acc, r) => {
      const status = r.status?.toUpperCase();
      if (status === 'SUCCESS') acc.SUCCESS++;
      else if (status === 'FAIL') acc.FAIL++;
      else acc.NOT_RUN++;
      acc.TOTAL++;
      return acc;
    }, { SUCCESS: 0, FAIL: 0, NOT_RUN: 0, TOTAL: 0 });

    return {
      items: filteredRequirements.map(r => ({
        code: r.code,
        name: r.name,
        status: r.status,
      })),
      counts,
    };
  }, [filteredRequirements]);

  if (!data) return null;

  return (
    <MainBox>
      {/* Title */}
      <Box sx={{ mb: 2 }}>
        <Title icon={fas.faTachometer} title="Test Dashboard" />
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Test Suite"
          value={selectedSuite}
          onChange={(e) => {
            setSelectedSuite(e.target.value);
            setSelectedTest('ALL');
            setSelectedRequirement('ALL');
          }}
        >
          <MenuItem value="ALL">All Suites</MenuItem>
          {data.suites.map((suite) => (
            <MenuItem key={suite.code} value={suite.code}>
              {suite.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          select
          variant="outlined"
          label="Test"
          value={selectedTest}
          onChange={(e) => {
            setSelectedTest(e.target.value);
            setSelectedRequirement('ALL');
          }}
        >
          <MenuItem value="ALL">All Tests</MenuItem>
          {availableTests.map((test) => (
            <MenuItem key={test.code} value={test.code}>
              {test.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          select
          variant="outlined"
          label="Requirement"
          value={selectedRequirement}
          onChange={(e) => {
            setSelectedRequirement(e.target.value);
          }}
        >
          <MenuItem value="ALL">All Requirements</MenuItem>
          {availableRequirements.map((req) => (
            <MenuItem key={req.code} value={req.code}>
              {req.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <SpacerLayout>
        <Spacer vertical weights={[1, 4]}>
          {/* Summary */}
          <TestKpiStrip
            runTsISO={data.runTsISO}
            tests={data.tests}
            requirements={data.requirements}
            suites={data.suites}
          />

          {/* Suties | Tests | Requirements */}
          <Spacer>
            <StatusColumn
              title="Test Suites"
              icon={fas.faBoxesStacked}
              data={suitesData}
            />

            <StatusColumn
              title="Tests"
              icon={fas.faVial}
              data={testsData}
            />

            <StatusColumn
              title="Requirements"
              icon={fas.faListCheck}
              data={requirementsData}
            />
          </Spacer>
        </Spacer>
      </SpacerLayout>
    </MainBox>
  );
};